/**
 * "Ring op" for a guest invite (spec §8).
 *
 * The host dials GUEST-<inviteId>. On the softphone it is a video call - SipClient.callGuest marks it so the camera
 * comes on after answer. On a desk phone it is an audio click-to-call, and the user is told that video needs the
 * softphone, because a desk phone cannot carry video and the guest expects to be seen.
 */

import SipClient from '@/utilities/SipClient'
import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'
import { sleep } from '@/utilities/Tools'
import _i18n from '@/utilities/I18n'
import { useApiStore } from '@/stores/Api'
import { useMyCallsStore } from '@/stores/MyCalls'
import { useSettingsStore } from '@/stores/Settings'

const i18n = _i18n.global

/**
 * Call the guest of an invite.
 * @param inviteId The invite's id
 * @param guestName The guest's name, shown on the call
 */
export async function ringUpGuest(inviteId: string, guestName: string): Promise<void> {
	const ss = useSettingsStore()
	const myCalls = useMyCallsStore()
	const target = `GUEST-${inviteId}`

	if (!ss.realPhoneId) {
		globalEvents.emit('CommonErrorModal', { header: i18n.t('common.error.noPhoneHeader'), message: i18n.t('common.error.noPhoneMessage') })
		return
	}

	if (ss.settings.softphoneId) {
		logger.info(`GuestCall: Calling guest ${guestName} as a video call via the softphone (${target})`)
		globalEvents.emit('Call:ActivateHold') // put a current call on hold first, as every softphone dial does
		await sleep(100)
		await SipClient.instance.callGuest(target, guestName)
		return
	}

	// Desk phone: audio only, and say so
	logger.info(`GuestCall: Calling guest ${guestName} as an audio call via the desk phone (${target}, phoneType ${myCalls.phoneType})`)

	try {
		await useApiStore().get(`/pbx/app/action/call?device=${ss.realPhoneId}&target=${target}`)
		globalEvents.emit('CommonToast', { severity: 'info', summary: i18n.t('guest.deskPhoneVideoHeader'), detail: i18n.t('guest.deskPhoneVideoMessage') })
	}
	catch (_) {
		globalEvents.emit('CommonErrorModal', { header: i18n.t('common.error.callFailedHeader'), message: i18n.t('common.error.callFailedMessage') })
	}
}
